import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ShieldCheck, ChevronLeft, RefreshCw, ShieldAlert, Lock, Gauge } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function CorexStatusManifest() {
  const { user } = useAuth();
  const [contenido, setContenido] = useState("");
  const [meta, setMeta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [estado, setEstado] = useState("ok"); // ok | forbidden | error

  const esAdmin = user && user.role === "admin";

  useEffect(() => {
    if (!esAdmin) { setCargando(false); return; }
    Promise.all([
      api.get("/corex-status/manifest", { responseType: "text" }),
      api.get("/corex-status/meta").catch(() => null),
    ])
      .then(([man, m]) => {
        const raw = typeof man.data === "string" ? man.data : String(man.data);
        // react-markdown no procesa HTML crudo: quitamos comentarios <!-- ... --> para que no se vean como texto.
        setContenido(raw.replace(/<!--[\s\S]*?-->/g, "").trimStart());
        if (m) setMeta(m.data);
      })
      .catch((e) => setEstado(e?.response?.status === 403 ? "forbidden" : "error"))
      .finally(() => setCargando(false));
  }, [esAdmin]);

  // Autorización cliente: si NO es admin, no se muestra información parcial.
  if (user && user !== false && !esAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="px-5 md:px-8 py-8 max-w-6xl mx-auto" data-testid="corex-status-page">
      <Link
        to="/software/corex"
        data-testid="corex-status-back"
        className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"
      >
        <ChevronLeft size={16} /> Volver al Workspace COREX
      </Link>

      <header className="mt-4 rounded-3xl overflow-hidden border border-slate-800 bg-gradient-to-br from-[#0B1220] to-[#111827] text-white p-8 md:p-10 relative">
        <div className="absolute -right-8 -top-8 opacity-10"><ShieldCheck size={200} /></div>
        <p className="text-xs font-bold uppercase tracking-widest text-emerald-300 inline-flex items-center gap-2">
          <Lock size={13} /> Documento de gobernanza · solo administración
        </p>
        <h1 className="mt-2 text-3xl md:text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
          <ShieldCheck size={34} className="text-emerald-300" /> Manifiesto Global COREX
        </h1>
        <p className="mt-3 max-w-2xl text-slate-300 text-base">
          Única Fuente de Verdad (SSoT) de la Familia ConstruLink. Cada cambio debe validarse
          contra este manifiesto para evitar regresiones de código o de cálculo.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-3 py-1.5 text-xs font-semibold text-emerald-200" data-testid="corex-status-cifra">
            <Gauge size={14} /> Cifra de control: S/ 73,797.29 · Casa Santisteban (rev. 18)
          </span>
          {meta?.actualizado_en && (
            <span className="inline-flex items-center gap-2 text-xs text-slate-400" data-testid="corex-status-actualizado">
              <RefreshCw size={13} /> Actualizado: {new Date(meta.actualizado_en).toLocaleString("es-PE")}
            </span>
          )}
        </div>
      </header>

      {cargando && <p className="mt-8 text-slate-400" data-testid="corex-status-loading">Cargando manifiesto…</p>}

      {estado === "forbidden" && (
        <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6 flex items-start gap-3" data-testid="corex-status-403">
          <ShieldAlert size={22} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="font-bold text-red-700">Acceso restringido</p>
            <p className="text-sm text-red-600">Este manifiesto es exclusivo del equipo de administración.</p>
          </div>
        </div>
      )}
      {estado === "error" && (
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="corex-status-error">
          No se pudo cargar el manifiesto. Verifica que <code>COREX_STATUS.md</code> exista en la raíz de la plataforma.
        </div>
      )}

      {estado === "ok" && !cargando && (
        <article className="mt-8 bg-white rounded-2xl border border-slate-100 shadow-soft p-6 md:p-10 min-w-0" data-testid="corex-status-contenido">
          <div className="cl-md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{contenido}</ReactMarkdown>
          </div>
        </article>
      )}
    </div>
  );
}
